import React, { useEffect, useMemo, useState } from 'react';
import { campaignAPI } from '../../services/api';
import { getPublicApiOrigin } from '../../config/env';

export const C = {
  brand: '#6366F1',
  brandLight: '#EEF2FF',
  brandDark: '#4338CA',
  surface: '#FFFFFF',
  border: '#E5E7EB',
  borderLight: '#F1F5F9',
  t1: '#111827',
  t2: '#475569',
  t3: '#6B7280',
  t4: '#9CA3AF',
  green: '#10B981',
  amber: '#F59E0B',
  red: '#EF4444',
  teal: '#0D9488',
  tealLight: '#F0FDFA',
};

export const SKILL_AREA_LABELS = {
  grammar: 'Grammar & Sentence Construction',
  vocabulary: 'Vocabulary in Context',
  reading_comprehension: 'Reading Comprehension',
  spoken_fluency: 'Spoken Fluency / Open Response',
};

export const SKILL_AREA_ORDER = ['grammar', 'vocabulary', 'reading_comprehension', 'spoken_fluency'];

export const LANGUAGE_LABELS = { en: 'English', es: 'Spanish' };

function diffBg(d) { return d === 'hard' ? '#FEE2E2' : d === 'easy' ? '#ECFDF5' : '#FEF3C7'; }
function diffColor(d) { return d === 'hard' ? '#991B1B' : d === 'easy' ? '#065F46' : '#92400E'; }

export function questionKey(q, idx) {
  return q.id != null ? String(q.id) : `idx-${idx}`;
}

/** Restricts a question pool to the category relevant to an exam mode: jd -> JD only, english -> Proficiency only, interview_coding -> AI/JD bank, combined (legacy) -> everything. */
export function filterPoolByExamType(pool, examType) {
  return pool.filter((q) => {
    const cat = q.exam_category || 'jd';
    if (examType === 'jd' || examType === 'interview_coding') return cat === 'jd';
    if (examType === 'english') return cat === 'english';
    return true; // combined (legacy) or unknown
  });
}

/** Splits a flat question array into { jdItems, englishBySkill }, each item annotated with `_idx` (position in the original array) and `_key` (stable key for selection). */
export function groupQuestions(questions) {
  const withIdx = questions.map((q, i) => ({ ...q, _idx: i, _key: questionKey(q, i) }));
  const jdItems = withIdx.filter((q) => (q.exam_category || 'jd') === 'jd');
  const englishBySkill = {};
  withIdx.filter((q) => q.exam_category === 'english').forEach((q) => {
    const key = SKILL_AREA_ORDER.includes(q.skill_area) ? q.skill_area : 'vocabulary';
    (englishBySkill[key] = englishBySkill[key] || []).push(q);
  });
  return { withIdx, jdItems, englishBySkill };
}

function QuestionCard({
  q, idx, isEnglish, questions, setQuestions,
  editingIdx, editingText, setEditingIdx, setEditingText,
  regeneratingIdx, onRegenerate,
  selectable, checked, onToggleCheck, readOnly, bulkSelect,
}) {
  const isEditingThis = editingIdx === idx;
  const showCheckbox = selectable || bulkSelect;
  const showActions = !selectable && !readOnly;

  const handleDelete = () => setQuestions(questions.filter((_, i) => i !== idx));

  const handleSaveEdit = () => {
    const updated = [...questions];
    updated[idx] = { ...updated[idx], question: editingText };
    setQuestions(updated);
    setEditingIdx(null);
  };

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: `1px solid ${isEditingThis ? C.brand : C.border}`,
        borderRadius: 12,
        padding: '16px 18px',
        marginBottom: 10,
        transition: 'border-color .2s',
        display: 'flex',
        gap: 12,
      }}
    >
      {showCheckbox && (
        <div style={{ paddingTop: 2 }}>
          <input
            type="checkbox"
            checked={!!checked}
            onChange={onToggleCheck}
            style={{ accentColor: C.brand, cursor: 'pointer', width: 16, height: 16 }}
          />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isEditingThis ? 12 : 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'white', background: isEnglish ? C.teal : C.brand, padding: '2px 10px', borderRadius: 20 }}>
              Q{idx + 1}
            </span>
            {isEnglish ? (
              <>
                <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: C.tealLight, color: C.teal }}>
                  {SKILL_AREA_LABELS[q.skill_area] || 'Proficiency'}
                </span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#EEF2FF', color: C.brandDark }}>
                  {LANGUAGE_LABELS[q.language] || LANGUAGE_LABELS.en}
                </span>
              </>
            ) : (
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: '#EEF2FF', color: '#3730A3' }}>
                {q.type}
              </span>
            )}
            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: diffBg(q.difficulty), color: diffColor(q.difficulty) }}>
              {q.difficulty}
            </span>
          </div>
          {showActions && (
            <div style={{ display: 'flex', gap: 8 }}>
              {isEditingThis ? (
                <>
                  <button type="button" onClick={handleSaveEdit}
                    style={{ fontSize: 11, color: 'white', background: C.green, border: 'none', padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                    ✓ Save
                  </button>
                  <button type="button" onClick={() => setEditingIdx(null)}
                    style={{ fontSize: 11, color: C.t2, background: 'none', border: `1px solid ${C.border}`, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => { setEditingIdx(idx); setEditingText(q.question); }}
                    style={{ fontSize: 11, color: C.t2, background: 'none', border: `1px solid ${C.border}`, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Edit
                  </button>
                  <button type="button" disabled={regeneratingIdx !== null} onClick={() => onRegenerate(idx)}
                    style={{ fontSize: 11, color: C.brand, background: 'none', border: `1px solid ${C.brand}`, padding: '4px 10px', borderRadius: 6, cursor: regeneratingIdx !== null ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: regeneratingIdx === idx ? 0.7 : 1 }}>
                    {regeneratingIdx === idx ? '…' : '↻ Regenerate'}
                  </button>
                  <button type="button" onClick={handleDelete}
                    style={{ fontSize: 11, color: C.red, background: 'none', border: '1px solid #FECACA', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
                    ✕
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {showActions && isEditingThis ? (
          <textarea
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            autoFocus
            style={{ width: '100%', minHeight: 80, border: `1.5px solid ${C.brand}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.t1, lineHeight: 1.6, fontFamily: 'inherit', outline: 'none', resize: 'vertical', background: '#FAFAFA', boxSizing: 'border-box' }}
          />
        ) : (
          <p style={{ fontSize: 13, color: C.t1, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{q.question}</p>
        )}
      </div>
    </div>
  );
}

function AddMenu({ color, onGenerateOne, generatingOne, onAddManual, generateLabel }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={generatingOne}
        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color, background: 'none', border: `1px solid ${color}`, padding: '4px 10px', borderRadius: 6, cursor: generatingOne ? 'wait' : 'pointer', fontFamily: 'inherit' }}
      >
        {generatingOne ? 'Generating…' : '+ Add'}
        {!generatingOne && <span style={{ fontSize: 9 }}>▾</span>}
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 20 }} onClick={() => setOpen(false)} />
          <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', background: 'white', border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: '0 6px 20px rgba(15,23,42,.12)', zIndex: 21, minWidth: 190, overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => { setOpen(false); onGenerateOne(); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 12, fontWeight: 600, color: C.t1, background: 'none', border: 'none', padding: '10px 14px', cursor: 'pointer', fontFamily: 'inherit' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F9FAFB'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              {generateLabel || '⚡ Generate with AI'}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); onAddManual(); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 12, fontWeight: 600, color: C.t1, background: 'none', border: 'none', padding: '10px 14px', cursor: 'pointer', fontFamily: 'inherit', borderTop: `1px solid ${C.borderLight}` }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F9FAFB'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              ✏️ Add Manually
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function SectionHeader({
  title, count, color, open, onToggle, onGenerateOne, generatingOne, onAddManual, generateLabel,
  onToggleSelectAll, selectAllChecked, selectedCount,
  onRegenerateSelected, regenBusy, regenProgress,
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        {onToggleSelectAll && count > 0 && (
          <input
            type="checkbox"
            checked={!!selectAllChecked}
            onChange={onToggleSelectAll}
            title="Select all in this section"
            style={{ accentColor: color, cursor: 'pointer', width: 14, height: 14, flexShrink: 0 }}
          />
        )}
        <button
          type="button"
          onClick={onToggle}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0,
            background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0',
            fontFamily: 'inherit', textAlign: 'left',
          }}
        >
          <span style={{ fontSize: 11, color, transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .15s', display: 'inline-block', flexShrink: 0 }}>
            ▶
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '.5px' }}>
            {title} ({count})
          </span>
        </button>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
        {onRegenerateSelected && selectedCount > 0 && (
          <button
            type="button"
            onClick={onRegenerateSelected}
            disabled={regenBusy}
            style={{ fontSize: 11, fontWeight: 700, color: 'white', background: regenBusy ? C.t4 : color, border: 'none', padding: '4px 10px', borderRadius: 6, cursor: regenBusy ? 'wait' : 'pointer', fontFamily: 'inherit' }}
          >
            {regenProgress ? `↻ ${regenProgress.done + 1}/${regenProgress.total}` : `↻ Regenerate (${selectedCount})`}
          </button>
        )}
        {onGenerateOne && (
          <AddMenu color={color} onGenerateOne={onGenerateOne} generatingOne={generatingOne} onAddManual={onAddManual} generateLabel={generateLabel} />
        )}
      </div>
    </div>
  );
}

/** `comments` (optional) is the recruiter's guidance for the replacement; it goes to the AI as feedback. */
export async function regenerateOne(q, campaign, existingTexts, comments) {
  const feedback = (comments || '').trim() || undefined;
  const isEnglish = q.exam_category === 'english';
  if (isEnglish) {
    // Keep regenerating in whichever language this item already tests, not
    // whatever the campaign's overall interview language happens to be.
    const res = await campaignAPI.regenerateEnglishQuestion({
      job_role: campaign.job_role || campaign.name,
      job_description: campaign.job_description,
      required_skills: campaign.required_skills || [],
      skill_area: q.skill_area || 'vocabulary',
      difficulty: q.difficulty,
      language: q.language || campaign.language || 'en',
      existing_question_texts: existingTexts,
      comments: feedback,
    });
    const rq = res.data?.question || {};
    return rq.question_text || q.question;
  }
  const base = getPublicApiOrigin();
  const token = localStorage.getItem('token');
  const res = await fetch(`${base}/api/campaigns/regenerate-question`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      role: campaign.job_role || campaign.name,
      job_description: campaign.job_description || '',
      required_skills: campaign.required_skills || [],
      difficulty: q.difficulty,
      type: q.type,
      language: campaign.language || 'en',
      existing: existingTexts,
      comments: feedback,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Regenerate failed');
  const rq = data.question || data;
  return rq.question_text || rq.question || q.question;
}

/** Calls the AI generator for one new question and returns it as a row — does not touch `questions`. */
export async function generateOneQuestion({ campaign, examCategory, questions, language, difficulty }) {
  const genLanguage = language || campaign?.language || 'en';
  const genDifficulty = difficulty || campaign.difficulty_level || 'medium';
  const { englishBySkill } = groupQuestions(questions);
  const existingTexts = questions.map((q) => q.question);
  if (examCategory === 'english') {
    // Pick whichever skill area currently has the fewest questions, for variety.
    const counts = SKILL_AREA_ORDER.map((area) => (englishBySkill[area] || []).length);
    const skillArea = SKILL_AREA_ORDER[counts.indexOf(Math.min(...counts))];
    const res = await campaignAPI.regenerateEnglishQuestion({
      job_role: campaign.job_role || campaign.name,
      job_description: campaign.job_description,
      required_skills: campaign.required_skills || [],
      skill_area: skillArea,
      difficulty: genDifficulty,
      language: genLanguage,
      existing_question_texts: existingTexts,
    });
    const rq = res.data?.question || {};
    return {
      question: rq.question_text || '',
      type: 'english',
      difficulty: genDifficulty,
      exam_category: 'english',
      skill_area: skillArea,
      language: genLanguage,
    };
  }
  const base = getPublicApiOrigin();
  const token = localStorage.getItem('token');
  const res = await fetch(`${base}/api/campaigns/regenerate-question`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      role: campaign.job_role || campaign.name,
      job_description: campaign.job_description || '',
      required_skills: campaign.required_skills || [],
      difficulty: genDifficulty,
      type: 'technical',
      language: campaign.language || 'en',
      existing: existingTexts,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Generate failed');
  const rq = data.question || data;
  return {
    question: rq.question_text || rq.question || '',
    type: 'technical',
    difficulty: genDifficulty,
    exam_category: 'jd',
  };
}

/** Shows one freshly-generated question so the user can approve, regenerate, or discard it before it's appended to the list. */
export function GeneratePreviewModal({ examCategory, row, loading, onCancel, onRegenerate, onConfirm }) {
  const isEnglish = examCategory === 'english';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 14, width: 520, maxWidth: '92vw', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.t1 }}>
            {isEnglish ? 'Generated Proficiency Question' : 'Generated JD Question'}
          </div>
          <button type="button" onClick={onCancel} disabled={loading} style={{ background: 'none', border: 'none', fontSize: 18, color: C.t3, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {isEnglish && row && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: C.tealLight, color: C.teal }}>
                {SKILL_AREA_LABELS[row.skill_area] || 'Proficiency'}
              </span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#EEF2FF', color: C.brandDark }}>
                {LANGUAGE_LABELS[row.language] || LANGUAGE_LABELS.en}
              </span>
            </div>
          )}
          <p style={{ fontSize: 13, color: C.t1, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', minHeight: 60, opacity: loading ? 0.5 : 1 }}>
            {loading ? 'Generating…' : (row?.question || '')}
          </p>
        </div>
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onCancel} disabled={loading}
            style={{ fontSize: 13, color: C.t3, background: 'none', border: `1px solid ${C.border}`, padding: '8px 16px', borderRadius: 8, cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
            Cancel
          </button>
          <button type="button" onClick={onRegenerate} disabled={loading}
            style={{ fontSize: 13, fontWeight: 600, color: C.brand, background: 'none', border: `1px solid ${C.brand}`, padding: '8px 16px', borderRadius: 8, cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
            ↻ Regenerate
          </button>
          <button type="button" onClick={onConfirm} disabled={loading}
            style={{ fontSize: 13, fontWeight: 700, color: 'white', background: loading ? C.t4 : C.brand, border: 'none', padding: '8px 18px', borderRadius: 8, cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
            + Add Question
          </button>
        </div>
      </div>
    </div>
  );
}

/** "Add Manually" modal: pick from the cross-campaign question bank, or write/paste new text. */
export function AddQuestionModal({ campaign, examCategory, language, questions, setQuestions, onClose, initialTab = 'bank' }) {
  const isEnglish = examCategory === 'english';
  const [tab, setTab] = useState(initialTab); // 'bank' | 'write'
  const [bankQuestions, setBankQuestions] = useState([]);
  const [loadingBank, setLoadingBank] = useState(true);
  const [bankError, setBankError] = useState('');
  const [selectedBankIds, setSelectedBankIds] = useState(new Set());
  const [bankSearch, setBankSearch] = useState('');
  const [pastedText, setPastedText] = useState('');
  const [pastedSkillArea, setPastedSkillArea] = useState('vocabulary');
  const [writeError, setWriteError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingBank(true);
      setBankError('');
      try {
        // Whole library, not just this job title: a role-filtered list only held this
        // position's own questions, which are already in the set and so filtered out.
        const res = await campaignAPI.getQuestionBank();
        const rows = res.data?.questions || [];
        const norm = (t) => String(t || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const seen = new Set(questions.map((q) => norm(q.question)));
        const role = norm(campaign.job_role || campaign.name);
        const filtered = rows.filter((r) => {
          if ((r.exam_category || 'jd') !== examCategory) return false;
          if (r.campaign_id && r.campaign_id === String(campaign.id)) return false;
          const key = norm(r.question_text);
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        // Same job title first, then everything else.
        const sameRole = (r) => (role && norm(r.job_role) === role ? 0 : 1);
        filtered.sort((a, b) => sameRole(a) - sameRole(b));
        if (!cancelled) setBankQuestions(filtered);
      } catch (err) {
        if (!cancelled) setBankError('Failed to load the question bank.');
      } finally {
        if (!cancelled) setLoadingBank(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaign.id, campaign.job_role, campaign.name, examCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const bankQuery = bankSearch.trim().toLowerCase();
  const visibleBank = bankQuery
    ? bankQuestions.filter((q) => `${q.question_text} ${q.job_role || ''}`.toLowerCase().includes(bankQuery))
    : bankQuestions;

  const toggleBankId = (id) => setSelectedBankIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleAddFromBank = () => {
    const chosen = bankQuestions.filter((q) => selectedBankIds.has(q.id));
    if (chosen.length === 0) return;
    const newRows = chosen.map((q) => ({
      question: q.question_text,
      type: q.question_type || (isEnglish ? 'english' : 'technical'),
      difficulty: q.difficulty || 'medium',
      exam_category: examCategory,
      ...(isEnglish ? { skill_area: q.skill_area || 'vocabulary', language: q.language || language || 'en' } : {}),
    }));
    setQuestions([...questions, ...newRows]);
    onClose();
  };

  const handleSavePasted = () => {
    const text = pastedText.trim();
    if (!text) { setWriteError('Please enter the question text.'); return; }
    setQuestions([...questions, {
      question: text,
      type: isEnglish ? 'english' : 'technical',
      difficulty: campaign.difficulty_level || 'medium',
      exam_category: examCategory,
      ...(isEnglish ? { skill_area: pastedSkillArea, language: language || 'en' } : {}),
    }]);
    onClose();
  };

  const tabBtn = (key, label) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      style={{
        flex: 1, fontSize: 12, fontWeight: 700, padding: '9px 10px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
        color: tab === key ? C.brand : C.t3,
        background: tab === key ? C.brandLight : 'none',
        borderBottom: `2px solid ${tab === key ? C.brand : 'transparent'}`,
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 14, width: 560, maxWidth: '92vw', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.t1 }}>
            Add {isEnglish ? 'Proficiency' : 'JD'} Question
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: C.t3, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ display: 'flex', marginTop: 12, borderBottom: `1px solid ${C.border}` }}>
          {tabBtn('write', 'Write / Paste New')}
          {tabBtn('bank', 'Pick from Question Library')}
        </div>

        {tab === 'bank' ? (
          <>
            <div style={{ padding: '14px 20px', overflowY: 'auto', flex: 1 }}>
              <input
                type="search"
                value={bankSearch}
                onChange={(e) => setBankSearch(e.target.value)}
                placeholder="Search questions or job titles…"
                aria-label="Search the question library"
                style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: 'inherit', marginBottom: 10 }}
              />
              {bankError && (
                <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 12, color: '#991B1B' }}>
                  {bankError}
                </div>
              )}
              {loadingBank ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: C.t4, fontSize: 13 }}>Loading question bank…</div>
              ) : visibleBank.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: C.t4, fontSize: 13 }}>
                  {bankQuery
                    ? `No library questions match “${bankSearch.trim()}”.`
                    : `No other ${isEnglish ? 'proficiency' : 'JD'} questions in the library yet.`}
                </div>
              ) : (
                visibleBank.map((q) => (
                  <label key={q.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 6px', borderBottom: `1px solid ${C.borderLight}`, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedBankIds.has(q.id)}
                      onChange={() => toggleBankId(q.id)}
                      style={{ accentColor: C.brand, cursor: 'pointer', marginTop: 3 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: C.t1, lineHeight: 1.5 }}>{q.question_text}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 7px', borderRadius: 20, background: diffBg(q.difficulty), color: diffColor(q.difficulty) }}>{q.difficulty || 'medium'}</span>
                        {q.job_role && (
                          <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 7px', borderRadius: 20, background: C.borderLight, color: C.t3 }}>{q.job_role}</span>
                        )}
                        {isEnglish && q.skill_area && (
                          <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 7px', borderRadius: 20, background: C.tealLight, color: C.teal }}>{SKILL_AREA_LABELS[q.skill_area] || q.skill_area}</span>
                        )}
                        {isEnglish && (
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: '#EEF2FF', color: C.brandDark }}>{LANGUAGE_LABELS[q.language] || LANGUAGE_LABELS.en}</span>
                        )}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
            <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" onClick={onClose}
                style={{ fontSize: 13, color: C.t3, background: 'none', border: `1px solid ${C.border}`, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddFromBank}
                disabled={selectedBankIds.size === 0}
                style={{ fontSize: 13, fontWeight: 700, color: 'white', background: selectedBankIds.size === 0 ? C.t4 : C.brand, border: 'none', padding: '8px 18px', borderRadius: 8, cursor: selectedBankIds.size === 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
              >
                Add Selected ({selectedBankIds.size})
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
              {writeError && (
                <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 12, color: '#991B1B' }}>
                  {writeError}
                </div>
              )}
              {isEnglish && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: C.t3, marginBottom: 5 }}>Skill area</label>
                  <select
                    value={pastedSkillArea}
                    onChange={(e) => setPastedSkillArea(e.target.value)}
                    style={{ fontSize: 12, padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 7, fontFamily: 'inherit', background: 'white' }}
                  >
                    {SKILL_AREA_ORDER.map((area) => (
                      <option key={area} value={area}>{SKILL_AREA_LABELS[area]}</option>
                    ))}
                  </select>
                </div>
              )}
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: C.t3, marginBottom: 5 }}>Question text</label>
              <textarea
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                autoFocus
                placeholder="Paste or write the question here…"
                style={{ width: '100%', minHeight: 140, border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.t1, lineHeight: 1.6, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" onClick={onClose}
                style={{ fontSize: 13, color: C.t3, background: 'none', border: `1px solid ${C.border}`, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePasted}
                style={{ fontSize: 13, fontWeight: 700, color: 'white', background: C.brand, border: 'none', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                OK, Add Question
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Renders a campaign's question pool grouped into a "JD" section and an
 * "English — <skill area>" set of sections, each collapsible.
 *
 * mode="browse": full edit/regenerate/delete per card (Questions tab). Pass
 *   bulkSelect+selectedKeys/onToggleKey to add checkboxes alongside the normal
 *   actions, plus a "Regenerate Selected" toolbar for multi-question regeneration.
 * mode="selectable": a checkbox per card instead of actions (Exam Set builder).
 * mode="readonly": no actions, no checkboxes (locked exam-set question view).
 */
export default function GroupedQuestionList({
  questions,
  campaign,
  mode = 'browse',
  setQuestions,
  selectedKeys,
  onToggleKey,
  emptyState,
  bulkSelect,
  onAddQuestion,
  language, // "en" | "es" — language new Proficiency questions get generated in
}) {
  const genLanguage = language || campaign?.language || 'en';
  const selectable = mode === 'selectable';
  const readOnly = mode === 'readonly';
  const [localError, setLocalError] = useState('');
  const [editingIdx, setEditingIdx] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [regeneratingIdx, setRegeneratingIdx] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null); // { done, total } while bulk-regenerating
  const [addBusy, setAddBusy] = useState(null); // 'jd' | 'english' while generating a new appended question
  const [addModalCategory, setAddModalCategory] = useState(null); // 'jd' | 'english' | null — drives the Add Manually modal
  const [previewGen, setPreviewGen] = useState(null); // { examCategory, row } | null — drives the generate-preview modal
  const [previewLoading, setPreviewLoading] = useState(false); // regenerating inside the preview modal
  // Sections start collapsed — user opens the ones they want to review.
  const [collapsed, setCollapsed] = useState({ jd: true, english: true });

  const { jdItems, englishBySkill } = useMemo(() => groupQuestions(questions), [questions]);
  const toggleSection = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  const openSection = (key) => setCollapsed((prev) => ({ ...prev, [key]: false }));

  const regenerateAt = async (idx) => {
    setRegeneratingIdx(idx);
    setLocalError('');
    try {
      const target = questions[idx];
      const existingTexts = questions.map((x) => x.question);
      const newText = await regenerateOne(target, campaign, existingTexts);
      setQuestions((prev) => {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], question: newText };
        return updated;
      });
    } catch (err) {
      setLocalError('Failed to regenerate question.');
    } finally {
      setRegeneratingIdx(null);
    }
  };

  const busy = regeneratingIdx !== null || bulkProgress !== null;

  const regenerateKeys = async (keys) => {
    const pool = [...jdItems, ...Object.values(englishBySkill).flat()];
    const targets = keys.map((key) => pool.find((q) => q._key === key)).filter(Boolean);
    if (targets.length === 0) return;
    setLocalError('');
    for (let i = 0; i < targets.length; i++) {
      setBulkProgress({ done: i, total: targets.length });
      // eslint-disable-next-line no-await-in-loop
      await regenerateAt(targets[i]._idx);
    }
    setBulkProgress(null);
  };

  const toggleSelectAll = (keys) => {
    if (!onToggleKey) return;
    const allSelected = keys.length > 0 && keys.every((k) => selectedKeys?.has(k));
    keys.forEach((k) => {
      const isSelected = selectedKeys?.has(k);
      if (allSelected ? isSelected : !isSelected) onToggleKey(k);
    });
  };

  const buildGeneratedRow = (examCategory) => generateOneQuestion({ campaign, examCategory, questions, language: genLanguage });

  /** Generates ONE new AI question and shows it in a preview modal — nothing is appended until the user confirms. */
  const generateForPreview = async (examCategory) => {
    setAddBusy(examCategory);
    setLocalError('');
    try {
      const row = await buildGeneratedRow(examCategory);
      setPreviewGen({ examCategory, row });
    } catch (err) {
      setLocalError('Failed to generate a new question.');
    } finally {
      setAddBusy(null);
    }
  };

  const regeneratePreview = async () => {
    if (!previewGen) return;
    setPreviewLoading(true);
    setLocalError('');
    try {
      const row = await buildGeneratedRow(previewGen.examCategory);
      setPreviewGen({ examCategory: previewGen.examCategory, row });
    } catch (err) {
      setLocalError('Failed to regenerate the question.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmAddPreview = () => {
    if (!previewGen) return;
    setQuestions([...questions, previewGen.row]);
    openSection(previewGen.examCategory === 'english' ? 'english' : 'jd');
    setPreviewGen(null);
  };

  const cardProps = {
    campaign, questions, setQuestions,
    editingIdx, editingText, setEditingIdx, setEditingText,
    regeneratingIdx, onRegenerate: regenerateAt,
    selectable, readOnly, bulkSelect,
  };

  const hasAny = jdItems.length > 0 || Object.keys(englishBySkill).length > 0;

  const jdKeys = jdItems.map((q) => q._key);
  const jdSelectedCount = selectedKeys ? jdKeys.filter((k) => selectedKeys.has(k)).length : 0;

  const englishAllItems = Object.values(englishBySkill).flat();
  const englishKeys = englishAllItems.map((q) => q._key);
  const englishSelectedCount = selectedKeys ? englishKeys.filter((k) => selectedKeys.has(k)).length : 0;

  const showSelection = bulkSelect && !!onToggleKey && !readOnly && !selectable;

  return (
    <div>
      {localError && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#991B1B' }}>
          {localError}
        </div>
      )}

      {!hasAny && emptyState}

      {(jdItems.length > 0 || (onAddQuestion && hasAny)) && (
        <div style={{ marginBottom: 20 }}>
          <SectionHeader
            title="JD Questions" count={jdItems.length} color={C.brand}
            open={!collapsed.jd} onToggle={() => toggleSection('jd')}
            onGenerateOne={onAddQuestion && !readOnly && !selectable ? () => generateForPreview('jd') : null}
            generatingOne={addBusy === 'jd'}
            generateLabel="⚡ Generate from JD"
            onAddManual={() => { setAddModalCategory('jd'); openSection('jd'); }}
            onToggleSelectAll={showSelection ? () => toggleSelectAll(jdKeys) : null}
            selectAllChecked={jdKeys.length > 0 && jdSelectedCount === jdKeys.length}
            selectedCount={showSelection ? jdSelectedCount : 0}
            onRegenerateSelected={showSelection ? () => regenerateKeys(jdKeys.filter((k) => selectedKeys.has(k))) : null}
            regenBusy={busy}
            regenProgress={bulkProgress}
          />
          {!collapsed.jd && jdItems.map((q) => (
            <QuestionCard
              key={q._key} q={q} idx={q._idx} isEnglish={false}
              checked={(selectable || bulkSelect) && selectedKeys ? selectedKeys.has(q._key) : false}
              onToggleCheck={() => onToggleKey && onToggleKey(q._key)}
              {...cardProps}
            />
          ))}
        </div>
      )}

      {(Object.keys(englishBySkill).length > 0 || (onAddQuestion && hasAny)) && (
        <div style={{ marginBottom: 20 }}>
          <SectionHeader
            title="Proficiency Questions"
            count={englishAllItems.length}
            color={C.teal}
            open={!collapsed.english}
            onToggle={() => toggleSection('english')}
            onGenerateOne={onAddQuestion && !readOnly && !selectable ? () => generateForPreview('english') : null}
            generatingOne={addBusy === 'english'}
            generateLabel="⚡ Generate Proficiency Question"
            onAddManual={() => { setAddModalCategory('english'); openSection('english'); }}
            onToggleSelectAll={showSelection ? () => toggleSelectAll(englishKeys) : null}
            selectAllChecked={englishKeys.length > 0 && englishSelectedCount === englishKeys.length}
            selectedCount={showSelection ? englishSelectedCount : 0}
            onRegenerateSelected={showSelection ? () => regenerateKeys(englishKeys.filter((k) => selectedKeys.has(k))) : null}
            regenBusy={busy}
            regenProgress={bulkProgress}
          />
          {!collapsed.english && SKILL_AREA_ORDER.filter((area) => (englishBySkill[area] || []).length > 0).map((area) => (
            <div key={area} style={{ marginBottom: 14, marginLeft: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 8 }}>
                {SKILL_AREA_LABELS[area]} ({englishBySkill[area].length})
              </div>
              {englishBySkill[area].map((q) => (
                <QuestionCard
                  key={q._key} q={q} idx={q._idx} isEnglish
                  checked={(selectable || bulkSelect) && selectedKeys ? selectedKeys.has(q._key) : false}
                  onToggleCheck={() => onToggleKey && onToggleKey(q._key)}
                  {...cardProps}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {addModalCategory && (
        <AddQuestionModal
          campaign={campaign}
          examCategory={addModalCategory}
          language={genLanguage}
          questions={questions}
          setQuestions={setQuestions}
          onClose={() => setAddModalCategory(null)}
        />
      )}

      {previewGen && (
        <GeneratePreviewModal
          examCategory={previewGen.examCategory}
          row={previewGen.row}
          loading={previewLoading}
          onCancel={() => setPreviewGen(null)}
          onRegenerate={regeneratePreview}
          onConfirm={confirmAddPreview}
        />
      )}
    </div>
  );
}
